/**
 * Notifications Component
 * 
 * Displays admin notifications:
 * - Invoice events
 * - Subscription events
 * - System alerts
 * 
 * Features:
 * - Notification list with filters
 * - Mark as read
 * - Trigger checks
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import { api } from '../../App';
import { toast } from 'sonner';
import {
  Bell,
  FileText,
  AlertTriangle,
  CheckCircle,
  Clock,
  CreditCard,
  Calendar,
  RefreshCw,
  Check,
  CheckCheck,
  MoreVertical,
  Filter,
  Zap,
  ExternalLink,
  XCircle,
  Info
} from 'lucide-react';

// Notification type icons
const TYPE_ICONS = {
  INVOICE_GENERATED: FileText,
  INVOICE_SENT: FileText,
  INVOICE_PAID: CheckCircle,
  INVOICE_OVERDUE: AlertTriangle,
  SUBSCRIPTION_EXPIRING: Calendar,
  SUBSCRIPTION_EXPIRED: XCircle,
  TRIAL_ENDING: Clock,
  AUTO_BILLING_COMPLETED: Zap,
  AUTO_BILLING_ERROR: AlertTriangle,
  SYSTEM_ALERT: Info
};

// Severity colors
const SEVERITY_COLORS = {
  INFO: 'bg-blue-50 text-blue-700 border-blue-200',
  SUCCESS: 'bg-green-50 text-green-700 border-green-200',
  WARNING: 'bg-orange-50 text-orange-700 border-orange-200',
  ERROR: 'bg-red-50 text-red-700 border-red-200'
};

const SEVERITY_BG = {
  INFO: 'bg-blue-100',
  SUCCESS: 'bg-green-100',
  WARNING: 'bg-orange-100',
  ERROR: 'bg-red-100'
};

// Type labels
const TYPE_LABELS = {
  INVOICE_GENERATED: 'Facture générée',
  INVOICE_SENT: 'Facture envoyée',
  INVOICE_PAID: 'Facture payée',
  INVOICE_OVERDUE: 'Facture en retard',
  SUBSCRIPTION_EXPIRING: 'Abonnement expire',
  SUBSCRIPTION_EXPIRED: 'Abonnement expiré',
  TRIAL_ENDING: 'Essai se termine',
  AUTO_BILLING_COMPLETED: 'Facturation auto',
  AUTO_BILLING_ERROR: 'Erreur facturation',
  SYSTEM_ALERT: 'Alerte système'
};

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [filters, setFilters] = useState({
    is_read: 'all',
    severity: 'all',
    type: 'all'
  });

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filters.is_read !== 'all') params.append('is_read', filters.is_read);
      if (filters.severity !== 'all') params.append('severity', filters.severity);
      if (filters.type !== 'all') params.append('type', filters.type);

      const { data } = await api.get(`/admin/notifications?${params}`);
      setNotifications(data.notifications);
      setPagination(data.pagination);
    } catch (error) {
      toast.error('Erreur lors du chargement des notifications');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const { data } = await api.get('/admin/notifications/summary');
      setSummary(data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  useEffect(() => {
    fetchNotifications();
    fetchSummary();
  }, [page, filters]);

  const handleMarkAsRead = async (id) => {
    try {
      await api.post(`/admin/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => 
        n.id === id ? { ...n, is_read: true, read_at: new Date() } : n
      ));
      fetchSummary();
      toast.success('Notification marquée comme lue');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const { data } = await api.post('/admin/notifications/read-all');
      toast.success(data.message);
      fetchNotifications();
      fetchSummary();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleCheckOverdue = async () => {
    try {
      const { data } = await api.post('/admin/notifications/check-overdue');
      toast.success(data.message);
      fetchNotifications();
      fetchSummary();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleCheckExpiring = async () => {
    try {
      const { data } = await api.post('/admin/notifications/check-expiring');
      toast.success(data.message);
      fetchNotifications();
      fetchSummary();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleNotificationClick = (notification) => {
    // Navigate to related entity
    if (notification.entity_type === 'Invoice' && notification.entity_id) {
      navigate(`/admin/billing?invoice=${notification.entity_id}`);
    } else if (notification.entity_type === 'Subscription' && notification.account_id) {
      navigate(`/admin/customers/${notification.account_id}`);
    }

    // Mark as read if unread
    if (!notification.is_read) {
      handleMarkAsRead(notification.id);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    const diff = now - d;
    
    if (diff < 60000) return 'À l\'instant';
    if (diff < 3600000) return `Il y a ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `Il y a ${Math.floor(diff / 3600000)}h`;
    if (diff < 604800000) return `Il y a ${Math.floor(diff / 86400000)}j`;
    
    return d.toLocaleDateString('fr-FR');
  };

  return (
    <div className="space-y-6" data-testid="notifications-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bell className="h-6 w-6" />
            Notifications
            {summary?.unread > 0 && (
              <Badge className="bg-red-500 text-white">{summary.unread}</Badge>
            )}
          </h1>
          <p className="text-muted-foreground">Alertes et événements système</p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Zap className="h-4 w-4 mr-2" />
                Actions
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={handleCheckOverdue}>
                <AlertTriangle className="h-4 w-4 mr-2" />
                Vérifier factures en retard
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleCheckExpiring}>
                <Calendar className="h-4 w-4 mr-2" />
                Vérifier abonnements expirant
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleMarkAllAsRead}>
                <CheckCheck className="h-4 w-4 mr-2" />
                Tout marquer comme lu
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={fetchNotifications} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilters(f => ({ ...f, is_read: 'false' }))}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-100">
                  <Bell className="h-4 w-4 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{summary.unread}</p>
                  <p className="text-xs text-muted-foreground">Non lues</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilters(f => ({ ...f, severity: 'WARNING' }))}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-100">
                  <AlertTriangle className="h-4 w-4 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{summary.bySeverity?.WARNING || 0}</p>
                  <p className="text-xs text-muted-foreground">Avertissements</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilters(f => ({ ...f, severity: 'ERROR' }))}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-100">
                  <XCircle className="h-4 w-4 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{summary.bySeverity?.ERROR || 0}</p>
                  <p className="text-xs text-muted-foreground">Erreurs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilters(f => ({ ...f, type: 'INVOICE_OVERDUE' }))}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-100">
                  <FileText className="h-4 w-4 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{summary.byType?.INVOICE_OVERDUE || 0}</p>
                  <p className="text-xs text-muted-foreground">Retards (7j)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilters(f => ({ ...f, type: 'SUBSCRIPTION_EXPIRING' }))}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <Calendar className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{summary.byType?.SUBSCRIPTION_EXPIRING || 0}</p>
                  <p className="text-xs text-muted-foreground">Expirant (7j)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <Select 
          value={filters.is_read} 
          onValueChange={(v) => { setFilters(f => ({ ...f, is_read: v })); setPage(1); }}
        >
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes</SelectItem>
            <SelectItem value="false">Non lues</SelectItem>
            <SelectItem value="true">Lues</SelectItem>
          </SelectContent>
        </Select>
        <Select 
          value={filters.severity} 
          onValueChange={(v) => { setFilters(f => ({ ...f, severity: v })); setPage(1); }}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Sévérité" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes</SelectItem>
            <SelectItem value="INFO">Info</SelectItem>
            <SelectItem value="SUCCESS">Succès</SelectItem>
            <SelectItem value="WARNING">Attention</SelectItem>
            <SelectItem value="ERROR">Erreur</SelectItem>
          </SelectContent>
        </Select>
        <Select 
          value={filters.type} 
          onValueChange={(v) => { setFilters(f => ({ ...f, type: v })); setPage(1); }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les types</SelectItem>
            <SelectItem value="INVOICE_GENERATED">Facture générée</SelectItem>
            <SelectItem value="INVOICE_OVERDUE">Facture en retard</SelectItem>
            <SelectItem value="INVOICE_PAID">Facture payée</SelectItem>
            <SelectItem value="SUBSCRIPTION_EXPIRING">Abonnement expire</SelectItem>
            <SelectItem value="TRIAL_ENDING">Essai se termine</SelectItem>
            <SelectItem value="AUTO_BILLING_COMPLETED">Facturation auto</SelectItem>
          </SelectContent>
        </Select>
        {(filters.is_read !== 'all' || filters.severity !== 'all' || filters.type !== 'all') && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => { setFilters({ is_read: 'all', severity: 'all', type: 'all' }); setPage(1); }}
          >
            Réinitialiser
          </Button>
        )}
      </div>

      {/* Notifications List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-violet-600" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-12">
              <Bell className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-muted-foreground">Aucune notification</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => {
                const Icon = TYPE_ICONS[notification.type] || Bell;
                return (
                  <div
                    key={notification.id}
                    className={`flex items-start gap-4 p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                      !notification.is_read ? 'bg-blue-50/30' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                    data-testid={`notification-${notification.id}`}
                  >
                    <div className={`p-2 rounded-lg ${SEVERITY_BG[notification.severity] || 'bg-gray-100'}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className={`font-medium ${!notification.is_read ? 'text-gray-900' : 'text-gray-600'}`}>
                            {notification.title}
                          </p>
                          <p className="text-sm text-muted-foreground mt-0.5">
                            {notification.message}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge variant="outline" className={SEVERITY_COLORS[notification.severity]}>
                            {TYPE_LABELS[notification.type] || notification.type}
                          </Badge>
                          {!notification.is_read && (
                            <div className="w-2 h-2 rounded-full bg-blue-500" />
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span>{formatDate(notification.createdAt)}</span>
                        {notification.entity_type && (
                          <span className="flex items-center gap-1">
                            <ExternalLink className="h-3 w-3" />
                            {notification.entity_type} #{notification.entity_id}
                          </span>
                        )}
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {!notification.is_read && (
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleMarkAsRead(notification.id); }}>
                            <Check className="h-4 w-4 mr-2" />
                            Marquer comme lu
                          </DropdownMenuItem>
                        )}
                        {notification.entity_type === 'Invoice' && (
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/admin/billing?invoice=${notification.entity_id}`); }}>
                            <FileText className="h-4 w-4 mr-2" />
                            Voir la facture
                          </DropdownMenuItem>
                        )}
                        {notification.account_id && (
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/admin/customers/${notification.account_id}`); }}>
                            <CreditCard className="h-4 w-4 mr-2" />
                            Voir le client
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {pagination.page} sur {pagination.pages} ({pagination.total} notifications)
          </p>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setPage(p => p - 1)} 
              disabled={page === 1}
            >
              Précédent
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setPage(p => p + 1)} 
              disabled={page >= pagination.pages}
            >
              Suivant
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
